import {arrayOfDatesToString, calculateCacheExpiration} from '@/shared/DateHelper'
import * as authPopup from '@/auth/authPopup'
import axios from 'axios'
import {checkForSessionInvitation, prepareEventsList} from '@/shared/Utilities'
import dayjs from 'dayjs'
import {getSessionsWithRegistrations, getTimeslotsByEvent} from '@/shared/SessionHelper'
import {setNavigationVisibility} from '@/shared/StateHelper'
import {useMsalStore} from '@/store/msal'
import {useApiStore} from '@/store/api'
import {useModal} from '@/store/modal'

const apiStore = useApiStore()
const msalStore = useMsalStore()
const modalStore = useModal()

const addEmptySessions = async function(sessions) {
    if (0 < sessions.length) {
        const eventId = sessions[0].event.id
        let timeslots = await getTimeslotsByEvent(eventId)
        for (let i = 0; i < timeslots.length; ++i) {
            timeslots[i].hasSessions = false
            for (let j = 0; j < sessions.length; ++j) {
                if (sessions[j].timeslot.id == timeslots[i].id) {
                    timeslots[i].hasSessions = true
                    break
                }
            }
        }

        let newId = -1
        for (let i = 0; i < timeslots.length; ++i) {
            if (false == timeslots[i].hasSessions) {
                let session = {
                    id: --newId,
                    name: ' ',
                    code: null,
                    short_description: null,
                    description: null,
                    graphic: null,
                    event: {
                        id: eventId,
                        name: sessions[0].event.name,
                    },
                    timeslot: timeslots[i],
                    presenters: [],
                    facilitators: [],
                    tech_facilitators: [],
                    captioners: [],
                    translators: [],
                    room: null,
                    tracks: [],
                    teleconferencing_information: null,
                    stream_url: null,
                    recording_url: null,
                    conflict: false,
                    mandatory: false,
                    is_published: false,
                    admin_notes: ''
                }
                sessions.push(session)
            }
        }
    }

    return sessions
}

// Adapted from https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa
export const callApi = async function(endpoint, method = 'GET', data = {}, authOnly = false, csrfToken = null) {
    let token = msalStore.getAccessToken
    if (authOnly && (null == token || '' == token)) {
        await authPopup.passTokenToApi()
        token = msalStore.getAccessToken
        if (null == token || '' == token) {
            console.warn('Authentication required for this API call')
            return null
        }
    }

    let headers = {}
    if (csrfToken) {
        headers = {'X-CSRFToken': csrfToken}
    }
    if (token) {
        headers.Authorization = `Bearer ${token}`
    }
    method = method.toUpperCase()
    let response = false

    try {
        switch (method) {
            case 'GET':
                response = await axios.get(endpoint, {headers: headers}).catch(errorHandler)
                break
            case 'POST':
                response = await axios.post(endpoint, data, {headers: headers}).catch(errorHandler)
                break
            case 'PUT':
                response = await axios.put(endpoint, data, {headers: headers}).catch(errorHandler)
                break
            case 'PATCH':
                response = await axios.patch(endpoint, data, {headers: headers}).catch(errorHandler)
                break
            case 'DELETE':
                response = await axios.delete(endpoint, {headers: headers}).catch(errorHandler)
                break
            default:
                response = await axios.get(endpoint, {headers: headers}).catch(errorHandler)
        }
        return response
    } catch (e) {
        return e.response
    }
}

const errorHandler = function(error) {
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('error.response.status: ' + error.response.status)
        console.error('error.response.data: ' + JSON.stringify(error.response.data))
        modalStore.open(error.response.data);
        if (404 == error.response.status) {
            window.location = '/notfound'
            return
        }
        if (399 < error.response.status && 500 > error.response.status) {
            // In the case of an authentication or authorization error, just sign out and force the auth to start over
            authPopup.signOut()
            window.location = '/?error=auth'
        }

    } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.error('error.request: ' + JSON.stringify(error.request))
        modalStore.open(error.request);
    } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error', error.message)
          modalStore.open(error.message);
    }
}

export const getAssetsByEventId = async function(eventId) {
    let assets = []
    const apiResponse = await callApi(apiStore.buildUrl('/events/assets/?event=' + eventId))
    if (apiResponse.data && Array.isArray(apiResponse.data)) {
        assets = apiResponse.data
    }

    return assets
}

export const getEventById = async function(eventId) {
    let currentEvent = apiStore.currentEvent
    if (null == currentEvent || eventId != currentEvent.event.id || dayjs() > currentEvent.expires) {
        apiStore.isVisibilitySet = false

        let idQueryString = ''
        if (null != apiStore.oid) {
            idQueryString = '?a=' + apiStore.oid
        }
        const eventIdWithOid = 0 < eventId ? eventId + '/' : ''

        const apiResponse = await callApi(apiStore.buildUrl('/events/events/' + eventIdWithOid + idQueryString))
        const event = apiResponse.data
        if (event.name) { // quick check that this probably is event data
            const expires = calculateCacheExpiration()

            apiStore.isAdmin = false
            if (null !== apiStore.currentAttendee) {
                for (let i = 0; i < event.admins.length; ++i) {
                    if (event.admins[i].id == apiStore.currentAttendee.id) {
                        console.log('Attendee ' + apiStore.currentAttendee.id + ' (' + apiStore.currentAttendee.display_name + ') is an admin for this event')
                        apiStore.isAdmin = true
                        break
                    }
                }
            }

            apiStore.currentEvent = {expires: expires, event: event}
            console.log('getEventById: Event ' + event.name + ' (' + eventId + ') found')
            storeSessionsByEventId(eventId).then(() => {setNavigationVisibility(event, apiStore)}) // kick this off in the background
            storePersonalScheduleByEventId(eventId, true) // likewise
            event.isCached = false
            event.dateString = arrayOfDatesToString(event.dates)
            apiStore.presenters = null

            return event

        } else {
            console.warn('getEventById: No such event (id ' + eventId + ')')
            return null
        }
    } else {
        console.log('getEventById: Event ' + currentEvent.event.name + ' (' + eventId + ') retrieved from cache')
        currentEvent.event.isCached = true
        return currentEvent.event
    }
}

export const getEventsByDateRange = async function(startDate, endDate, filterByUser = false, sortDescending = false) {
    let queryString = 'startDate=' + startDate.format('YYYY-MM-DD') + '&endDate=' + endDate.format('YYYY-MM-DD')
    if (apiStore.isStaff) {
        queryString += '&staff=true'
    }
    if (filterByUser) {
        queryString += '&filter=true'
    }
    if (null !== apiStore.oid) {
        queryString += '&a=' + apiStore.oid
    }
    const apiResponse = await callApi(apiStore.buildUrl('/events/events/?' + queryString))
    const events = apiResponse.data

    if (Array.isArray(events)) { // quick check that this probably is events data
        return prepareEventsList(events, apiStore.isStaff, sortDescending)
    } else {
        return []
    }
}

export const getNonInstructionalSessionsByEventId = async function(eventId) {
    const apiResponse = await callApi(apiStore.buildUrl('/events/sessions/?event=' + eventId + '&instructional=false'))
    return apiResponse.data
}

export const showExternalEntities = async function(eventId, entityType) {
    const apiResponse = await callApi(apiStore.buildUrl('/events/' + entityType + '/?event=' + eventId))
    return (Array.isArray(apiResponse.data) && 0 < apiResponse.data.length)
}

export const showFacilitatorGuide = function() {
    let isFacilitator = false

    if(!apiStore.currentEvent.event.has_facilitator_guide){
        return isFacilitator
    }

    if (apiStore.currentEvent.sessions && apiStore.currentAttendee) {
        outterloop: /* I can't belive JS has a goto all of this feels wrong */
        for (let session_idx  = 0; session_idx < apiStore.currentEvent.sessions.length; session_idx++) {
            for (let facilitator_idx  = 0; facilitator_idx< apiStore.currentEvent.sessions[session_idx].facilitators.length; facilitator_idx++) {
                if (apiStore.currentEvent.sessions[session_idx].facilitators[facilitator_idx].id == apiStore.currentAttendee.id) {
                        isFacilitator = true
                        break outterloop
                }
            }
        }
    }

    return isFacilitator
}

export const getFacilitatorGuide = async function(eventId) {
    if( apiStore.facilitatorGuide ){
        return apiStore.facilitatorGuide
    }
    const apiResponse = await callApi(apiStore.buildUrl('/events/facilitatorguide/?event=' + eventId))
    apiStore.facilitatorGuide = apiResponse.data[0]['facilitator_guide']
}

export const getPresentersByEventId = async function(eventId) {
    if (null == apiStore.presenters) {
        const apiResponse = await callApi(apiStore.buildUrl('/events/presenters/?event=' + eventId))
        const presenters = apiResponse.data
        apiStore.presenters = presenters
    }
    return apiStore.presenters
}

export const storePersonalScheduleByEventId = async function(eventId, includeNonInstructional) {
    let sessions = await getSessionsWithRegistrations(eventId, true)
    if (false === Array.isArray(sessions)) { // quick check that this probably is events data
        throw new Error('Failed reading personal schedule sessions')
    }

    if (includeNonInstructional) {
        const nonInstructionalSessions = await getNonInstructionalSessionsByEventId(eventId)
        for (let i = 0; i < nonInstructionalSessions.length; ++i) {
            let isInSesions = false
            for (let j = 0; j < sessions.length; ++j) {
                if (sessions[j].id == nonInstructionalSessions[i].id) {
                    isInSesions = true
                    break
                }
            }

            if (false == isInSesions) {
                sessions.push(nonInstructionalSessions[i])
            }
        }
    }

    sessions = sessions.filter((session) => null != session.timeslot) // remove sessions with no timeslot
    sessions = sessions.filter((session) => session.is_published) // remove unpublished sessions
    if (false == apiStore.isStaff && apiStore.currentAttendee) { // remove invitation-only sessions to which this attendee is not invited
        sessions = checkForSessionInvitation(sessions, apiStore.currentAttendee.id)
    }

    if (0 < sessions.length) {
        const today = dayjs()
        const visibleDate = sessions[0].event.visible_date ? dayjs(sessions[0].event.visible_date) : today
        const visibility = today >= visibleDate
        for(let i = 0; i < sessions.length; ++i){
            sessions[i]['showRoom'] = visibility
            sessions[i]['showMaterial'] = visibility
        }
    }

    const expires = calculateCacheExpiration()
    apiStore.mySchedule = {expires: expires, sessions: sessions}
}

export const storeSessionsByEventId = async function(eventId) {
    let sessions = await getSessionsWithRegistrations(eventId)
    sessions = sessions.filter((session) => null != session.timeslot)
    if (apiStore.isStaff) {
        sessions = await addEmptySessions(sessions)
    } else {
        sessions = sessions.filter((session) => session.is_published)
        sessions = checkForSessionInvitation(sessions, apiStore.oid)
    }

    if (apiStore.currentEvent) {
        apiStore.currentEvent.sessions = sessions
        apiStore.showFacilitatorGuideLink = showFacilitatorGuide()
    }
}

export const uploadFileToApi = async function(endpoint, file) {
    const formData = new FormData()
    formData.append('name', file.name ? file.name : 'Temporary name')
    formData.append('description', file.description ? file.description : 'Temporary description')
    formData.append('short_description', file.description ? file.description : 'Temporary description')
    formData.append('uri', file.file)
    if(file.session){
       formData.append('session', file.session )
    }
    if(file.presenter){
       formData.append('presenter', file.presenter )
    }
    const response = await callApi(endpoint, 'POST', formData)
    if (response && 200 <= response.status && 209 >= response.status){
        return response.data
    } else {
        return {'error': response.data}
    }
}

export const uploadFilesToApi = async function(endpoint, files) {
    const assets = []
    for (let i = 0; i < files.length; ++i) {
        const response = await uploadFileToApi(endpoint, files[i])
        assets.push(response)
        files[i].isUploaded = response.name && files[i].name == response.name
    }

    return assets
}
